// Geometry for the combined equity + underwater chart (DWLF-306; canvas board BacktestResults).
// Presentation only (charter invariant 8): it draws the API's equity curve and the depth below each
// running peak. The max-drawdown NUMBER on the page is the API's, never read from this.

const toTime = (d) => new Date(d).getTime();

/**
 * @param {{date: string, equity: number}[]} curve  the API equity curve, in date order
 * @param {{width: number, equityHeight: number, ddHeight: number, gap: number}} box
 * @returns {null | {equityPath, ddPath, xOf, yOf, minEquity, maxEquity, maxDepthPct, t0, t1, ddTop}}
 */
export function buildEquityGeometry(curve, { width, equityHeight, ddHeight, gap }) {
  const points = (curve || []).filter((p) => Number.isFinite(p?.equity) && p?.date);
  if (points.length < 2) return null;

  const t0 = toTime(points[0].date);
  const t1 = toTime(points[points.length - 1].date);
  const span = Math.max(1, t1 - t0);
  const equities = points.map((p) => p.equity);
  const minEquity = Math.min(...equities);
  const maxEquity = Math.max(...equities);
  const pad = (maxEquity - minEquity) * 0.08 || maxEquity * 0.01 || 1;
  const lo = minEquity - pad;
  const hi = maxEquity + pad;

  const xOf = (date) => ((toTime(date) - t0) / span) * width;
  const yOf = (equity) => ((hi - equity) / (hi - lo)) * equityHeight;

  let peak = -Infinity;
  const depths = points.map((p) => {
    peak = Math.max(peak, p.equity);
    return peak > 0 ? ((p.equity - peak) / peak) * 100 : 0; // ≤ 0, percent below the running peak
  });
  const maxDepthPct = Math.min(0, ...depths);
  const ddTop = equityHeight + gap;
  const ddScale = maxDepthPct < 0 ? ddHeight / -maxDepthPct : 0;
  const yDd = (depth) => ddTop + -depth * ddScale;

  const equityPath = points.map((p, i) => `${i ? 'L' : 'M'}${xOf(p.date).toFixed(1)} ${yOf(p.equity).toFixed(1)}`).join(' ');
  const ddLine = points.map((p, i) => `L${xOf(p.date).toFixed(1)} ${yDd(depths[i]).toFixed(1)}`).join(' ');
  const ddPath = `M0 ${ddTop} ${ddLine} L${width} ${ddTop} Z`;

  return { equityPath, ddPath, xOf, yOf, minEquity, maxEquity, maxDepthPct, t0, t1, ddTop, lo, hi };
}

/** Evenly spaced value ticks for the equity axis. */
export const equityTicks = (lo, hi, count = 4) =>
  Array.from({ length: count }, (_, i) => lo + ((hi - lo) * (i + 0.5)) / count);
